import React, { useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  DialogTitle,
  Typography,
  Divider,
} from '@mui/material'
import { Upload, FileCopy } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import { useImportDataMutation } from '../../store/redux/reducers/worklogSlice'
import { useAppSelector, useNotification } from '../../store/redux/hooks'
import { createErrorMessage } from '../../store/utils'
import { User } from '../../store/types'
import { SquareButton } from '../../styles/ButtonStyles'
import { useImportDetailDataMutation } from '../../store/redux/reducers/importSlice'
import { useGetImportDictionaryQuery } from '../../store/redux/reducers/dictionarySlice'
import DateRangePickerWithPresets from '../DateRangePickerWithPresets/DateRangePickerWithPresets'
import { getDefaultImportType, getImportTypesForProject } from '../../store/utils/DictionaryUtils'
import { BaseDialogProps } from './BaseDialogProps'
import { getSelectedDate } from '../../store/redux/reducers/monthPickerSlice'

interface FileImportDialogProps extends BaseDialogProps {
  selectedUser: User | null
  disabled: boolean
  fullWidth?: boolean
}

const FileImportDialog: React.FC<FileImportDialogProps> = ({ selectedUser, disabled, fullWidth, open, onClose }) => {
  const { t } = useTranslation()
  const { displayNotification } = useNotification()
  const [tabIndex, setTabIndex] = useState(0)
  const date = useAppSelector(state => getSelectedDate(state))

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  const ownFileInputRef = useRef<HTMLInputElement>(null)
  const customFileInputRef = useRef<HTMLInputElement>(null)
  const [ownFile, setOwnFile] = useState<File | null>(null)
  const [importOwnData, { isLoading: isOwnLoading }] = useImportDataMutation()

  const [customFields, setCustomFields] = useState<{
    file: File | null
    dateFrom: Dayjs | null
    dateTo: Dayjs | null
    overwrite: boolean
    project: string
    importType: string
  }>({
    file: null,
    dateFrom: dayjs().startOf('month'),
    dateTo: dayjs().endOf('month'),
    overwrite: false,
    project: '',
    importType: '',
  })

  const [importDetailData, { isLoading: isCustomLoading }] = useImportDetailDataMutation()
  const { data: importDictionary } = useGetImportDictionaryQuery()

  useEffect(() => {
    if (open) {
      setOwnFile(null)
      setCustomFields({
        file: null,
        dateFrom: dayjs(date).startOf('month'),
        dateTo: dayjs(date).endOf('month'),
        overwrite: false,
        project: '',
        importType: '',
      })
      if (ownFileInputRef.current) ownFileInputRef.current.value = ''
      if (customFileInputRef.current) customFileInputRef.current.value = ''
    }
  }, [open, date])

  const handleOwnFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (file) setOwnFile(file)
  }

  const handleCustomFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setCustomFields(prev => ({ ...prev, file }))
  }

  const handleProjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const project = e.target.value
    setCustomFields(prev => ({
      ...prev,
      project,
      importType: getDefaultImportType(importDictionary, project),
    }))
  }

  const handleImportTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFields(prev => ({ ...prev, importType: e.target.value }))
  }

  const handleOverwriteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFields(prev => ({ ...prev, overwrite: e.target.checked }))
  }

  const handleConfirmOwnImport = async () => {
    if (ownFile && selectedUser) {
      try {
        const arrayBuffer = await ownFile.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)
        await importOwnData({ data: uint8Array, employeeId: selectedUser.id }).unwrap()
        displayNotification({ message: t('page.worklog.importSuccess'), type: 'success' })
        setOwnFile(null)
        if (ownFileInputRef.current) {
          ownFileInputRef.current.value = ''
        }
        if (onClose) {
          onClose()
        }
      } catch (error) {
        displayNotification({ message: createErrorMessage(error) || t('page.worklog.importFailed'), type: 'error' })
      }
    }
  }

  const handleConfirmCustomImport = async () => {
    if (customFields.file && selectedUser) {
      try {
        const extension = customFields.file.name.split('.').pop()
        if (!extension) return
        const arrayBuffer = await customFields.file.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)
        let binary = ''

        for (let i = 0; i < uint8Array.length; i++) {
          binary += String.fromCharCode(uint8Array[i])
        }

        const base64String = window.btoa(binary)
        const imported = await importDetailData({
          data: base64String,
          employeeId: selectedUser.id,
          importType: customFields.importType,
          projectName: customFields.project,
          overwrite: customFields.overwrite,
          dateFrom: customFields.dateFrom ? dayjs(customFields.dateFrom).format('YYYY-MM-DD') : '',
          dateTo: customFields.dateTo ? dayjs(customFields.dateTo).format('YYYY-MM-DD') : '',
          extension,
        }).unwrap()
        if (imported === 0) {
          displayNotification({
            message: t('page.worklog.customImportZeroRecords'),
            type: 'error',
          })
        } else {
          displayNotification({
            message: t('page.worklog.customImportSuccess', { records: imported }),
            type: 'success',
          })
        }
        setCustomFields({
          file: null,
          dateFrom: dayjs().startOf('month'),
          dateTo: dayjs().endOf('month'),
          overwrite: false,
          project: '',
          importType: '',
        })

        if (customFileInputRef.current) {
          customFileInputRef.current.value = ''
        }

        if (onClose) {
          onClose()
        }
      } catch (error) {
        displayNotification({ message: createErrorMessage(error) || t('page.worklog.importFailed'), type: 'error' })
      }
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth={fullWidth} PaperProps={{ sx: { minWidth: 400 } }}>
      <DialogTitle>{t('page.worklog.import')}</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth">
          <Tab label={t('page.worklog.fileImport.importOwn')} />
          <Tab label={t('page.worklog.fileImport.importCustom')} />
        </Tabs>
        {tabIndex === 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<Upload />}
              onClick={() => ownFileInputRef.current?.click()}
              disabled={disabled || isOwnLoading}
            >
              {t('page.worklog.fileImport.selectFile')}
            </Button>
            {ownFile && <Box sx={{ mt: 2, wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{ownFile.name}</Box>}
            <input
              type="file"
              onChange={handleOwnFileChange}
              accept=".xlsx, .xls"
              style={{ display: 'none' }}
              ref={ownFileInputRef}
              disabled={disabled}
            />
            <Divider sx={{ pt: 2 }} />
            <Typography sx={{ fontStyle: 'italic', my: 2, color: 'warning.main', textAlign: 'center' }}>
              {t('page.adminsPage.generalSettings.groupImport.dialog.overwriteWarning')}
            </Typography>
          </Box>
        )}
        {tabIndex === 1 && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={() => customFileInputRef.current?.click()}
              disabled={disabled || isCustomLoading}
            >
              {t('page.worklog.fileImport.selectFile')}
            </Button>
            {customFields.file && (
              <Box sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{customFields.file.name}</Box>
            )}
            <input
              type="file"
              onChange={handleCustomFileChange}
              accept=".xlsx, .xls, .csv"
              style={{ display: 'none' }}
              ref={customFileInputRef}
              disabled={disabled}
            />
            <DateRangePickerWithPresets
              startDate={customFields.dateFrom}
              endDate={customFields.dateTo}
              setStartDate={date => setCustomFields(prev => ({ ...prev, dateFrom: date }))}
              setEndDate={date => setCustomFields(prev => ({ ...prev, dateTo: date }))}
              startDateLabel={t('components.dateRangePickerWithPresets.dateRange.startDate')}
              endDateLabel={t('components.dateRangePickerWithPresets.dateRange.endDate')}
              suggestedDate={dayjs(date)}
            />
            <TextField
              select
              label={t('page.worklog.fileImport.project')}
              value={customFields.project}
              onChange={handleProjectChange}
              disabled={disabled || isCustomLoading}
            >
              {importDictionary && Object.keys(importDictionary).length > 0 ? (
                Object.keys(importDictionary).map(project => (
                  <MenuItem key={project} value={project}>
                    {project}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  {t('page.worklog.noProjects')}
                </MenuItem>
              )}
            </TextField>
            <TextField
              select
              label={t('page.worklog.fileImport.importType')}
              value={customFields.importType}
              onChange={handleImportTypeChange}
              disabled={
                disabled ||
                isCustomLoading ||
                !customFields.project ||
                (importDictionary && getImportTypesForProject(importDictionary, customFields.project).length === 1)
              }
            >
              {getImportTypesForProject(importDictionary, customFields.project).length > 0 ? (
                getImportTypesForProject(importDictionary, customFields.project).map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  {t('page.worklog.noImportTypes')}
                </MenuItem>
              )}
            </TextField>
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={
                <Checkbox
                  checked={customFields.overwrite}
                  onChange={handleOverwriteChange}
                  disabled={disabled || isCustomLoading}
                />
              }
              label={t('page.worklog.fileImport.overwrite')}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>
          {t('components.dialog.cancel')}
        </Button>
        {tabIndex === 0 ? (
          <LoadingButton
            autoFocus
            variant="contained"
            onClick={handleConfirmOwnImport}
            loading={isOwnLoading}
            disabled={disabled || !ownFile}
          >
            {t('components.dialog.confirm')}
          </LoadingButton>
        ) : (
          <LoadingButton
            onClick={handleConfirmCustomImport}
            autoFocus
            variant="contained"
            loading={isCustomLoading}
            disabled={
              disabled ||
              !customFields.file ||
              !customFields.project ||
              !customFields.importType ||
              !customFields.dateFrom ||
              !customFields.dateTo
            }
          >
            {t('components.dialog.confirm')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  )
}

interface FileImportButtonProps {
  selectedUser: User | null
  disabled: boolean
  fullWidth?: boolean
  squareButton?: boolean
}

const FileImportButton: React.FC<FileImportButtonProps> = ({
  selectedUser,
  disabled,
  fullWidth,
  squareButton = false,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  return (
    <>
      {squareButton ? (
        <Tooltip title={t('page.worklog.import')}>
          <SquareButton
            onClick={handleOpen}
            disabled={disabled}
            color="secondary"
            size="medium"
            aria-label={t('page.worklog.import')}
          >
            <Upload />
          </SquareButton>
        </Tooltip>
      ) : (
        <Button
          onClick={handleOpen}
          disabled={disabled}
          variant="contained"
          color="secondary"
          size="medium"
          startIcon={<FileCopy />}
        >
          {t('page.worklog.import')}
        </Button>
      )}
      <FileImportDialog
        selectedUser={selectedUser}
        disabled={disabled}
        fullWidth={fullWidth}
        open={open}
        onClose={handleClose}
      />
    </>
  )
}

export default FileImportButton
